/**
 * Лобби: категории столов (взнос N), открытые столы и их заполненность, краткая сводка
 * профиля (история и статистика — в профиле), проверка файла. Сведения о площадке (режим,
 * источник данных, счёт банка) — только в «Администрировании».
 */
import { useState } from 'react';
import type { RoomSummary, ServerInfo, TableCategory } from '@mt/protocol';
import type { SignedPackage } from '@mt/verification';
import { client, useApp } from '../net/client';
import { cr, pts, secondsLeft, STAGE_TITLES, useNow } from '../lib/format';
import { Link } from '../lib/router';
import { Avatar, pct, signedCr, toneOf } from './kit';
import { Rules } from './Rules';
import { VerifyDialog } from './VerifyDialog';

export function Lobby() {
  const info = useApp((s) => s.info);
  const lobby = useApp((s) => s.lobby);
  const profile = useApp((s) => s.profile);
  const [rules, setRules] = useState(false);
  const [verify, setVerify] = useState<SignedPackage | null | undefined>(undefined);
  const now = useNow(500);
  if (!info || !profile) return null;

  const playing = lobby.reduce((a, r) => a + r.seated, 0);
  const st = profile.stats;

  return (
    <div className="lobby">
      <header className="lobby-head">
        <div>
          <h1>Лобби</h1>
          <p className="muted-line">
            Прогноз движения скрытого индекса из трёх акций S&amp;P 500: сначала только график, затем шкала, и лишь в последнем круге — состав.
          </p>
        </div>
        <dl className="head-stats">
          <div>
            <dt>Баланс</dt>
            <dd className="mono">{cr(profile.balance)}</dd>
          </div>
          <div>
            <dt>За столами</dt>
            <dd className="mono">{playing}</dd>
          </div>
          <div>
            <dt>Открыто столов</dt>
            <dd className="mono">{lobby.length}</dd>
          </div>
        </dl>
      </header>

      <div className="lobby-grid">
        <section className="lobby-main">
          <div className="section-head">
            <h2>Столы</h2>
            <button className="btn tiny ghost" onClick={() => setRules(true)}>
              Правила и расчёт выигрыша
            </button>
          </div>
          <div className="categories">
            {info.categories.map((cat) => (
              <CategoryCard key={cat.id} cat={cat} info={info} rooms={lobby.filter((r) => r.categoryId === cat.id)} balance={profile.balance} seatedRoomId={profile.seatedRoomId} now={now} />
            ))}
          </div>

          <div className="section-head">
            <h2>Как проходит розыгрыш</h2>
          </div>
          <ol className="flow">
            <li>
              <b>Подготовка</b>
              <span>взнос N в банк, хеш состава опубликован</span>
            </li>
            <li>
              <b>Круг 1 · график</b>
              <span>без шкалы цен; выбор направления</span>
            </li>
            <li>
              <b>Круг 2 · шкала</b>
              <span>цены и расстояния в пунктах</span>
            </li>
            <li>
              <b>Круг 3 · состав</b>
              <span>три акции раскрыты; число пунктов</span>
            </li>
            <li>
              <b>Целевая свеча</b>
              <span>P₁ — закрытие следующей минуты</span>
            </li>
            <li>
              <b>Расчёт и проверка</b>
              <span>выплаты по точности; пакет проверки</span>
            </li>
          </ol>
        </section>

        <aside className="lobby-side">
          <section className="panel profile-card">
            <div className="pc-head">
              <Avatar id={profile.id} name={profile.name} size={44} />
              <div>
                <b>{profile.name}</b>
                <span className="mono">{cr(profile.balance)}</span>
              </div>
            </div>
            <p className="muted-line small">Игровые кредиты: нельзя купить, продать, передать или обменять.</p>
            {profile.canRestore && (
              <button className="btn wide" onClick={() => client.restore()}>
                Восстановить стартовый баланс {cr(info.startingBalance)}
              </button>
            )}
            <dl className="stat-tiles">
              <div>
                <dt>Розыгрышей</dt>
                <dd className="mono">{st.games}</dd>
              </div>
              <div>
                <dt>Винрейт</dt>
                <dd className="mono">{pct(st.winRate)}</dd>
              </div>
              <div>
                <dt>Итог</dt>
                <dd className={`mono ${toneOf(st.net)}`}>{signedCr(st.net)}</dd>
              </div>
              <div>
                <dt>Средняя ошибка</dt>
                <dd className="mono">{st.avgError === null ? '—' : pts(st.avgError)}</dd>
              </div>
            </dl>
            <Link to="/profile" className="btn wide">
              Профиль, история и статистика
            </Link>
          </section>

          <section className="panel">
            <div className="section-head compact">
              <h3>Проверка розыгрыша</h3>
            </div>
            <p className="muted-line">
              После каждого розыгрыша участник может скачать подписанный пакет: все ходы, состав и исходные котировки акций, расчёт и проводки. Его можно проверить здесь или утилитой{' '}
              <code>npm run verify</code> и сравнить котировки с любым внешним источником.
            </p>
            <button className="btn wide" onClick={() => setVerify(null)}>
              Проверить файл пакета…
            </button>
          </section>
        </aside>
      </div>
      {rules && <Rules info={info} onClose={() => setRules(false)} />}
      {verify !== undefined && <VerifyDialog initial={verify} onClose={() => setVerify(undefined)} />}
    </div>
  );
}

function CategoryCard(props: { cat: TableCategory; info: ServerInfo; rooms: RoomSummary[]; balance: number; seatedRoomId: string | null; now: number }) {
  const { cat, info, rooms, now } = props;
  const enough = props.balance >= cat.ante;
  const seatedHere = rooms.some((r) => r.roomId === props.seatedRoomId);
  const seated = rooms.reduce((a, r) => a + r.seated, 0);
  const cap = info.commissionCapAntesPerRound;
  return (
    <article className={`panel category ${seatedHere ? 'mine' : ''}`}>
      <header>
        <span className="cat-label">{cat.title}</span>
        <span className="cat-ante mono">
          {cat.ante / 100}
          <small> кр.</small>
        </span>
        <span className="muted-line">взнос N</span>
      </header>
      <dl className="cat-meta">
        <div>
          <dt>Игроков</dt>
          <dd>
            {cat.minPlayers}–{cat.maxPlayers}
          </dd>
        </div>
        <div>
          <dt>Круги</dt>
          <dd>3 × {Math.round(info.stageMs / 1000)} с</dd>
        </div>
        <div>
          <dt>Комиссия</dt>
          <dd>
            {info.commissionBps / 100} %{cap ? ` · ≤ ${cap}N` : ''}
          </dd>
        </div>
      </dl>
      <div className="cat-rooms">
        <div className="cat-rooms-head">
          <span>Столы</span>
          <span className="dim">{rooms.length ? `${rooms.length} · игроков ${seated}` : 'нет открытых'}</span>
        </div>
        {rooms.length === 0 ? (
          <p className="muted-line">Будет создан новый стол.</p>
        ) : (
          <ul className="room-list">
            {rooms.map((r) => (
              <RoomRow key={r.roomId} r={r} now={now} mine={r.roomId === props.seatedRoomId} />
            ))}
          </ul>
        )}
      </div>
      <button className="btn primary wide" disabled={!enough} onClick={() => client.join(cat.id)}>
        {seatedHere ? 'Вернуться за стол' : 'Сесть за стол'}
      </button>
      {!enough && <p className="notice warn">Недостаточно кредитов для взноса.</p>}
    </article>
  );
}

function RoomRow({ r, now, mine }: { r: RoomSummary; now: number; mine: boolean }) {
  const left = secondsLeft(r.nextStartAt, now);
  const status =
    r.status === 'playing'
      ? r.stage
        ? STAGE_TITLES[r.stage]
        : 'идёт розыгрыш'
      : r.status === 'countdown'
        ? `старт через ${left} с`
        : r.status === 'results'
          ? 'расчёт'
          : `готовы ${r.ready} из ${r.minPlayers}`;
  return (
    <li className={`room-row ${mine ? 'mine' : ''}`}>
      <span className="seat-dots" aria-label={`занято ${r.seated} из ${r.maxPlayers}`}>
        {Array.from({ length: r.maxPlayers }, (_, i) => (
          <i key={i} className={i < r.ready ? 'ready' : i < r.seated ? 'taken' : ''} />
        ))}
      </span>
      <span className={`room-status ${r.status}`}>{status}</span>
      <button className="btn tiny ghost" onClick={() => client.watch(r.roomId)}>
        {mine ? 'Открыть' : 'Смотреть'}
      </button>
    </li>
  );
}
